'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import type { ChatMessage as ChatMessageType, ToolCall } from '@/context/AIContext'
import { PdfDownloadCard } from './PdfDownloadCard'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// ============================================================================
// Markdown renderer (extended: bold, italic, code, h2/h3, blockquote, lists)
// ============================================================================

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>,
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>,
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>,
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')

  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const trimmedLine = line.trim()

        if (!trimmedLine) return <div key={i} className="h-1" />

        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        if (/^\d+\.\s/.test(trimmedLine)) {
          const m = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (m) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
                  {m[1]}.
                </span>
                <span>{renderInlineMarkdown(m[2])}</span>
              </div>
            )
          }
        }

        return (
          <p key={i}>
            {renderInlineMarkdown(trimmedLine)}
          </p>
        )
      })}
    </div>
  )
}

// ============================================================================
// PDF tool call detector
// ============================================================================

interface PdfToolResult {
  filename: string
  download_url: string
  title?: string
  status?: string
}

const PDF_TOOL_NAMES = new Set([
  'generate_pdf',
  'generate_report',
  'export_tickets_pdf',
])

function extractPdfDownload(toolCalls?: ToolCall[]): PdfToolResult | null {
  if (!toolCalls) return null
  for (const tc of toolCalls) {
    if (
      PDF_TOOL_NAMES.has(tc.name) &&
      tc.result &&
      typeof tc.result === 'object'
    ) {
      const r = tc.result as Record<string, unknown>
      if (r.status === 'generated' && typeof r.download_url === 'string') {
        return {
          filename: (r.filename as string) || 'document.pdf',
          download_url: r.download_url as string,
          title: (r.title as string | undefined) || (tc.args?.title as string | undefined),
        }
      }
    }
  }
  return null
}

// ============================================================================
// Loading indicator
// ============================================================================

function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 py-1" aria-label="AI is thinking" role="status">
      <div className="flex gap-1" aria-hidden="true">
        <span
          className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60"
          style={{ animationDelay: '0ms' }}
        />
        <span
          className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60"
          style={{ animationDelay: '150ms' }}
        />
        <span
          className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60"
          style={{ animationDelay: '300ms' }}
        />
      </div>
      <span className="text-sm text-brand-muted">Thinking…</span>
    </div>
  )
}

// ============================================================================
// ChatMessage component
// ============================================================================

export function ChatMessage({ message, userName, userImage }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading

  const pdfDownload = !isUser ? extractPdfDownload(message.toolCalls) : null

  return (
    <div
      className={cn('flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}
    >
      {/* Avatar */}
      <div className="flex-shrink-0" aria-hidden="true">
        {isUser ? (
          <Avatar src={userImage} fallback={userName || 'U'} size="sm" />
        ) : (
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full',
              'bg-gradient-to-br from-brand-navy to-brand-purple shadow-soft',
            )}
          >
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Content column */}
      <div
        className={cn(
          'flex max-w-[85%] flex-col gap-1',
          isUser ? 'items-end' : 'items-start',
        )}
      >
        {/* Bubble */}
        <div
          className={cn(
            'rounded-2xl px-4 py-3 transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
          )}
          role={isUser ? 'none' : 'article'}
          aria-label={isUser ? undefined : 'AI assistant response'}
        >
          {isLoading ? (
            <ThinkingIndicator />
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {/* Timestamp */}
        <span
          className={cn(
            'text-[10px] text-muted-foreground/60',
            isUser ? 'pr-1' : 'pl-1',
          )}
          aria-label={`Sent at ${formatTime(message.timestamp)}`}
        >
          {formatTime(message.timestamp)}
        </span>

        {/* PDF download card */}
        {pdfDownload && (
          <PdfDownloadCard
            filename={pdfDownload.filename}
            downloadUrl={pdfDownload.download_url}
            title={pdfDownload.title}
          />
        )}
      </div>
    </div>
  )
}
